import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import projectService from '../../services/projectService';
import RichTextEditor from '../Common/RichTextEditor';
import './ProjectForm.css';
import vendorService from '../../services/vendorService';
import portfolioService from '../../services/portfolioService';


const ProjectForm = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    status: 'Planning',
    priority: 'Medium',
    start_date: '',
    end_date: '',
    budget_allocated: '',
    budget_spent: '',
    progress_percentage: 0,
    vendor_id: '',
    portfolio_id: '',
  });

  useEffect(() => {
    fetchVendors()
    fetchPortfolios()
    if (isEdit && id) {
      fetchProject();
    }
  }, [isEdit, id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProjectById(id);
      const project = data.project;
      setFormData({
        name: project.name || '',
        code: project.code || '',
        description: project.description || '',
        status: project.status || 'Planning',
        priority: project.priority || 'Medium',
        start_date: project.start_date ? project.start_date.split('T')[0] : '',
        end_date: project.end_date ? project.end_date.split('T')[0] : '',
        budget_allocated: project.budget_allocated || '',
        budget_spent: project.budget_spent || '',
        vendor_id: project.vendor_id || '',
        portfolio_id: project.portfolio.id || '',
        progress_percentage: project.progress_percentage || 0
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch project');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const data = await vendorService.getAllVendors();
      setVendors(data.vendors || data.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch vendors');
      setVendors([]); // Ensure vendors is always an array
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolios = async () => {
    try {
      setLoading(true);
      const data = await portfolioService.getAllPortfolios();

      setPortfolios(data.portfolios || []);

      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch portfolios');
      setPortfolios([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (content) => {
    setFormData(prev => ({ ...prev, description: content }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        budget_allocated: formData.budget_allocated ? parseFloat(formData.budget_allocated) : null,
        budget_spent: formData.budget_spent ? parseFloat(formData.budget_spent) : null,
        progress_percentage: parseInt(formData.progress_percentage) || 0,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };

      if (isEdit) {
        await projectService.updateProject(id, submitData);
      } else {
        await projectService.createProject(submitData);
      }

      navigate('/app/projects');
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${isEdit ? 'update' : 'create'} project`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/app/projects');
  };

  if (loading && isEdit) {
    return <div className="loading">Loading project...</div>;
  }

  return (
    <div className="project-form-container">
      <div className="project-form-header">
        <h1>{isEdit ? 'Edit Project' : 'Create New Project'}</h1>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit} className="project-form">
        <div className="form-row custom-row">
          <div className="form-group ">
            <label htmlFor="name">Project Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-control"
              required
              placeholder="Enter project name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="code">Project Code *</label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              className="form-control"
              required
              placeholder="e.g., PROJ-001"
            />
          </div>
        </div>

        <div className='mb-4'>
          <div className="form-group ">
            <RichTextEditor
              value={formData.description}
              onChange={handleDescriptionChange}
              placeholder="Enter detailed project description with formatting..."
              height="300px"
            />
          </div>
        </div>

        <div className="form-row custom-row">
          <div className="form-group ">
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="Planning">Planning</option>
              <option value="In Progress">In Progress</option>
              <option value="On Hold">On Hold</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div className="form-group ">
            <label htmlFor="priority">Priority *</label>
            <select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Critical">Critical</option>
            </select>
          </div>
        </div>

        <div className="form-row custom-row">
          <div className="form-group">
            <label htmlFor="start_date">Start Date</label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="end_date">End Date</label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="form-row custom-row">
          <div className="form-group">
            <label htmlFor="budget_allocated">Budget Allocated (₹)</label>
            <input
              type="number"
              id="budget_allocated"
              name="budget_allocated"
              value={formData.budget_allocated}
              onChange={handleChange}
              className="form-control"
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="budget_spent">Budget Spent (₹)</label>
            <input
              type="number"
              id="budget_spent"
              name="budget_spent"
              value={formData.budget_spent}
              onChange={handleChange}
              className="form-control"
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="form-group">
            <label htmlFor="progress_percentage">Progress (%)</label>
            <input
              type="number"
              id="progress_percentage"
              name="progress_percentage"
              value={formData.progress_percentage}
              onChange={handleChange}
              className="form-control"
              min="0"
              max="100"
              placeholder="0"
            />
          </div>
          <div className="form-group ">
            <label htmlFor="vendor">Tenant</label>
            <select
              id="vendor"
              name="vendor_id"
              value={formData.vendor_id}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="">Select Tenant</option>
              {vendors.map(p => (
                <option key={p.id} value={p.id}>
                  {p.company_name} ({p.email})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group ">
            <label htmlFor="portfolio_id">Portfolio </label>
            <select
              id="portfolio_id"
              name="portfolio_id"
              value={formData.portfolio_id}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="">Select Portfolio</option>
              {portfolios.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Project' : 'Create Project')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;


